import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiPlus, FiUsers, FiLock, FiMail, FiX, FiActivity, FiClock, FiUserCheck, FiLayers, FiTarget } from "react-icons/fi";
import Select from "react-select";
import AppLayout from "../components/layout/AppLayout";
import Button from "../components/common/Button";
import Chip from "../components/common/Chip";
import PageHeader from "../components/common/PageHeader";
import {
  Select as FormSelect,
  labelClass,
  inputClass,
  textareaClass,
} from "../components/common/FormControls";
import ConfirmDialog from "../components/common/ConfirmDialog";
import CompanyAccessPanel from "../components/common/CompanyAccessPanel";
import {
  professionalAssignmentAPI,
  companiesAPI,
  professionalInvitationAPI,
} from "../services/api";
import { useUI } from "../context/UIContext";
import { getView, setView, hasView } from "../services/viewCache";
import { markOnboardingStep } from "../utils/onboarding";
import InviteProfessionalModal from "../components/common/InviteProfessionalModal";

/* ── Stale-while-revalidate keys (services/viewCache) ──────────────────────
   This route unmounts on navigation, so the assignment lists refetched from
   zero on return and flashed a skeleton over rows the user just saw. The keys
   fold in every param each list depends on; on mount the filters/page are
   always at their defaults, so the seeds below read the default-signature key
   and the fetchers read/write the live one. ─────────────────────────────── */
const assignmentsKey = (page, per, status, proStatus) =>
  `manageCollab:assignments:${page}|${per}|${status}|${proStatus}`;
const revokedKey = (page, per) => `manageCollab:revoked:${page}|${per}`;
const ACCESS_COUNT_KEY = "manageCollab:accessCount";

/* ── An assignment's `professional` ref is null when the underlying user was
   deleted, leaving an orphaned assignment. Render those rows with a safe
   placeholder instead of crashing on `.fullName` / `.email` inside the list
   .map() (which white-screens the whole page). ─────────────────────────── */
const FALLBACK_PROFESSIONAL = {
  _id: null,
  fullName: "Unknown professional",
  profession: "—",
  email: "—",
};
const withSafeProfessional = (a) => ({
  ...a,
  professional: a?.professional || FALLBACK_PROFESSIONAL,
});

/* ── Assignment status badges ─────────────────────────────────────────── */
const STATUS_BADGE = {
  active: { bg: "#e8efe5", color: "#4a6b3f", label: "Active" },
  inactive: { bg: "#f2e9e6", color: "#9a564a", label: "Inactive" },
  suspended: { bg: "#f3ebd7", color: "#8a6a1f", label: "Suspended" },
};

const PROFESSIONAL_STATUS_BADGE = {
  pending: { bg: "#f3ebd7", color: "#8a6a1f", label: "Pending" },
  accepted: { bg: "#e8efe5", color: "#4a6b3f", label: "Accepted" },
  rejected: { bg: "#f2e9e6", color: "#9a564a", label: "Rejected" },
};

const ACCESS_LEVEL_BADGE = {
  read: { bg: "#edeef3", color: "#3a4a66", label: "Read" },
  read_write: { bg: "#edeef3", color: "#3a4a66", label: "Read/Write" },
  admin: { bg: "#e8efe5", color: "#4a6b3f", label: "Admin" },
};

const ASSIGNMENT_SCOPE_BADGE = {
  all_companies: { bg: "#edeef3", color: "#3a4a66", label: "All Companies" },
  specific_companies: {
    bg: "#efe9e0",
    color: "#6f6049",
    label: "Specific Companies",
  },
};

/* ── React Select Custom Styles ───────────────────────────────────────── */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: "#ddd6c6",
    backgroundColor: "#fff",
    fontSize: "12px",
    minHeight: "32px",
    boxShadow: state.isFocused ? "0 0 0 1px #145f5f" : "none",
    "&:hover": {
      borderColor: "#145f5f",
    },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "12px",
    backgroundColor: state.isFocused ? "#f8f5ef" : "#fff",
    color: "#08111f",
    padding: "8px 12px",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#e4f5f3",
    border: "1px solid #145f5f",
    borderRadius: "4px",
    fontSize: "11px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#145f5f",
    fontWeight: "500",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#145f5f",
    "&:hover": {
      backgroundColor: "#145f5f",
      color: "#fff",
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: "#7a7060",
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: "#7a7060",
    fontSize: "12px",
  }),
};

/* ── Format date for display ─────────────────────────────────────────── */
function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ═══════════════════════════════════════════════════════════════════ */

export default function ManageCollaborators() {
  const { showToast } = useUI();
  const location = useLocation();
  const navigate = useNavigate();

  // Getting-started runway: visiting completes "Invite your CA/CS".
  useEffect(() => { markOnboardingStep('visited-collaborators'); }, []);

  // Deep-link support: profile → "Invite New Professional" opens the create
  // modal directly instead of just landing on the list.
  useEffect(() => {
    if (location.state?.openCreate) {
      setShowCreateModal(true);
      window.history.replaceState({}, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  /* ── State ───────────────────────────────────────────────────────── */
  const [assignments, setAssignments] = useState(() => getView(assignmentsKey(1, 10, "", ""))?.list ?? []);
  const [revokedAssignments, setRevokedAssignments] = useState(() => getView(revokedKey(1, 10))?.list ?? []);
  const [availableProfessionals, setAvailableProfessionals] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  // Full skeleton only on a true first load; a cached list renders instantly.
  const [loading, setLoading] = useState(() => !hasView(assignmentsKey(1, 10, "", "")));
  const [revokedLoading, setRevokedLoading] = useState(false);
  const [professionalsLoading, setProfessionalsLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [deletingAssignment, setDeletingAssignment] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    professionalId: [],
    assignmentScope: "specific_companies",
    accessLevel: "read",
    assignedCompanies: [],
    validFrom: "",
    validUntil: "",
    notes: "",
  });

  // Search states
  const [professionalSearch, setProfessionalSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [professionalStatusFilter, setProfessionalStatusFilter] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(() => getView(assignmentsKey(1, 10, "", ""))?.pagination ?? {
    pages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Tab state
  const [activeTab, setActiveTab] = useState("active");
  // Count for the "Company Access" tab badge (pending + active grants).
  const [accessCount, setAccessCount] = useState(() => getView(ACCESS_COUNT_KEY) ?? 0);

  /* ── Load assignments ─────────────────────────────────────────────── */
  const fetchAssignments = useCallback(async () => {
    const key = assignmentsKey(page, itemsPerPage, statusFilter, professionalStatusFilter);
    // Full skeleton only for a view we've never loaded; a cached one refreshes
    // silently underneath.
    if (!hasView(key)) setLoading(true);
    try {
      const params = { page, limit: itemsPerPage };
      if (statusFilter) params.status = statusFilter;
      if (professionalStatusFilter)
        params.professionalStatus = professionalStatusFilter;

      const res = await professionalAssignmentAPI.getMyAssignments(params);
      if (res?.success) {
        const list = (res.data?.assignments || [])
          .filter(Boolean)
          .map(withSafeProfessional);
        const pg = res.pagination || {
          pages: 1,
          total: 0,
          hasNext: false,
          hasPrev: false,
        };
        setAssignments(list);
        setPagination(pg);
        setView(key, { list, pagination: pg });
      }
    } catch (err) {
      // Keep the cached list on a failed refresh; only surface the toast when
      // there's genuinely nothing to show.
      if (!hasView(key)) showToast(err.message || "Failed to load assignments", "error");
    } finally {
      setLoading(false);
    }
  }, [page, itemsPerPage, statusFilter, professionalStatusFilter, showToast]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  // When the AI agent confirms an assignment elsewhere, refetch so this list stays
  // in sync with the backend without a manual reload.
  useEffect(() => {
    const onAgentAction = (e) => {
      if (e?.detail?.action === 'assign_professional') fetchAssignments();
    };
    window.addEventListener('cs:agent-action-executed', onAgentAction);
    return () => window.removeEventListener('cs:agent-action-executed', onAgentAction);
  }, [fetchAssignments]);

  // Seed the Company Access tab count on mount (kept live by the panel below).
  useEffect(() => {
    companiesAPI.access
      .requests()
      .then((res) => {
        if (res?.success) {
          const count = (res.data || []).length;
          setAccessCount(count);
          setView(ACCESS_COUNT_KEY, count);
        }
      })
      .catch(() => {});
  }, []);

  /* ── Load revoked assignments ─────────────────────────────────────── */
  const [revokedPage, setRevokedPage] = useState(1);
  const [revokedPagination, setRevokedPagination] = useState(() => getView(revokedKey(1, 10))?.pagination ?? {
    pages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  });

  const fetchRevokedAssignments = useCallback(async () => {
    const key = revokedKey(revokedPage, itemsPerPage);
    if (!hasView(key)) setRevokedLoading(true);
    try {
      const res = await professionalAssignmentAPI.getRevokedAccess({
        page: revokedPage,
        limit: itemsPerPage,
      });
      if (res?.success) {
        const list = (res.data?.assignments || [])
          .filter(Boolean)
          .map(withSafeProfessional);
        const pg = res?.data.pagination || {
          pages: 1,
          total: 0,
          hasNext: false,
          hasPrev: false,
        };
        setRevokedAssignments(list);
        setRevokedPagination(pg);
        setView(key, { list, pagination: pg });
      }
    } catch (err) {
      // Keep the cached revoked list on a failed refresh.
      console.error("Failed to load revoked assignments:", err);
      if (!hasView(key)) showToast(err.message || "Failed to load revoked assignments", "error");
    } finally {
      setRevokedLoading(false);
    }
  }, [showToast, revokedPage, itemsPerPage]);

  useEffect(() => {
    fetchRevokedAssignments();
  }, [fetchRevokedAssignments]);

  /* ── Load available professionals ─────────────────────────────────── */
  const fetchAvailableProfessionals = useCallback(async (search = "") => {
    setProfessionalsLoading(true);
    try {
      const res = await professionalInvitationAPI.searchProfessionals({
        q: search,
      });
      if (res?.success) {
        const professionals = res.data?.professionals || [];
        // Transform for react-select
        const options = professionals
          .filter((prof) => prof && prof._id) // Filter out invalid entries
          .map((prof) => ({
            value: prof._id,
            label: `${prof.fullName || "Unknown"} - ${prof.profession || "Not specified"}`,
            fullName: prof.fullName || "Unknown",
            email: prof.email || "",
            profession: prof.profession || "Not specified",
            mobile: prof.mobile || "",
            status: prof.status || "ACTIVE",
          }));
        setAvailableProfessionals(options);
      }
    } catch (err) {
      console.error("Failed to load professionals:", err);
    } finally {
      setProfessionalsLoading(false);
    }
  }, []);

  /* ── Invitation handlers ─────────────────────────────────────────── */
  const handleProfessionalCreated = useCallback(
    (professional) => {
      showToast("Professional invited successfully!", "success");
    },
    [showToast],
  );

  const handleProfessionalSelected = useCallback((professional) => {
    // Auto-select the professional in the form
    const option = {
      value: professional._id,
      label: `${professional.fullName} - ${professional.profession}`,
      fullName: professional.fullName,
      email: professional.email,
      profession: professional.profession,
      mobile: professional.mobile,
      status: professional.status,
    };

    setFormData((prev) => ({
      ...prev,
      professionalId: [option],
    }));

    setShowInviteModal(false);
  }, []);

  useEffect(() => {
    if (showCreateModal || showEditModal) {
      setAvailableProfessionals([]);
    }
  }, [showCreateModal, showEditModal]);

  /* ── Load available companies ───────────────────────────────────────── */
  const fetchAvailableCompanies = useCallback(async (search = "") => {
    setCompaniesLoading(true);
    try {
      const res = await companiesAPI.getAll({ search });
      if (res?.success) {
        // Handle both data structures: res.data.companies or res.data directly
        const companies = res.data?.companies || res.data || [];
        // Ensure it's an array
        const companiesArray = Array.isArray(companies) ? companies : [];

        // Transform for react-select
        const options = companiesArray
          .filter((company) => company && company._id)
          .map((company) => ({
            value: company._id,
            label: `${company.name || "Unknown Company"} (${company.cin || "N/A"})`,
            name: company.name || "Unknown Company",
            cin: company.cin || "N/A",
            status: company.status || "Unknown",
          }));
        setAvailableCompanies(options);
      }
    } catch (err) {
      console.error("Failed to load companies:", err);
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showCreateModal || showEditModal) {
      fetchAvailableCompanies("");
    }
  }, [showCreateModal, showEditModal, fetchAvailableCompanies]);

  /* ── Form handlers ─────────────────────────────────────────────────── */
  const resetForm = useCallback(() => {
    setFormData({
      professionalId: [],
      assignmentScope: "specific_companies",
      accessLevel: "read",
      assignedCompanies: [],
      validFrom: "",
      validUntil: "",
      notes: "",
    });
    setProfessionalSearch("");
    setCompanySearch("");
  }, []);

  // Synchronous re-entry guards (refs, not state — a state flag wouldn't update
  // in time to block a double-click): submittingRef serializes create/update/
  // delete; rowBusyRef serializes the inline status / access-level dropdowns so
  // rapid changes don't storm the server + race the "already assigned" check.
  const submittingRef = useRef(false);
  const rowBusyRef = useRef(false);

  const handleCreateAssignment = useCallback(async () => {
    if (!formData.professionalId || formData.professionalId.length === 0) {
      showToast("Please select at least one professional", "error");
      return;
    }

    if (
      formData.assignmentScope === "specific_companies" &&
      formData.assignedCompanies.length === 0
    ) {
      showToast("Please select at least one company", "error");
      return;
    }

    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      // Create assignments for each selected professional
      const assignmentData = {
        ...formData,
        professionalId: formData.professionalId.map(
          (professional) => professional.value,
        ),
        assignedCompanies: formData.assignedCompanies.map((company) => ({
          company: company.value,
          accessLevel: company.accessLevel || "read",
        })),
        validFrom: formData.validFrom || new Date().toISOString(),
      };

      const result =
        await professionalAssignmentAPI.createAssignment(assignmentData);

      if (result?.success) {
        const createdCount = result.data?.assignments?.length || 1;
        showToast(
          `${createdCount} assignment${createdCount > 1 ? "s" : ""} created successfully`,
          "success",
        );
        setShowCreateModal(false);
        resetForm();
        fetchAssignments();
      } else {
        // Handle API-level errors (success: false)
        const errorMessage =
          result?.message || result?.error || "Failed to create assignments";
        showToast(errorMessage, "error");
      }
    } catch (err) {
      showToast("Failed to create assignments", "error");
    } finally {
      submittingRef.current = false;
    }
  }, [formData, showToast, resetForm, fetchAssignments]);

  const handleUpdateAssignment = useCallback(async () => {
    if (!selectedAssignment) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    try {
      const updateData = {
        assignmentScope: formData.assignmentScope,
        accessLevel: formData.accessLevel,
        validFrom: formData.validFrom,
        validUntil: formData.validUntil,
        notes: formData.notes,
      };

      if (formData.assignmentScope === "specific_companies") {
        updateData.assignedCompanies = formData.assignedCompanies.map(
          (company) => ({
            company: company.value,
            accessLevel: company.accessLevel || "read",
          }),
        );
      }

      const res = await professionalAssignmentAPI.updateAssignment(
        selectedAssignment._id,
        updateData,
      );
      if (res?.success) {
        showToast("Assignment updated successfully", "success");
        setShowEditModal(false);
        setSelectedAssignment(null);
        resetForm();
        fetchAssignments();
      } else {
        showToast(res?.message || "Failed to update assignment", "error");
      }
    } catch (err) {
      console.error("Update assignment error:", err);

      // Extract meaningful error message from APIError
      let errorMessage = "Failed to update assignment";

      if (err?.originalData?.error) {
        errorMessage = err.originalData.error;
      } else if (err?.originalData?.message) {
        errorMessage = err.originalData.message;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.details?.message) {
        errorMessage = err.details.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      }

      // Show specific error messages for common issues
      if (errorMessage.includes("not found")) {
        showToast("Assignment not found or may have been deleted", "error");
      } else if (
        errorMessage.includes("not owned") ||
        errorMessage.includes("permission")
      ) {
        showToast(
          "You do not have permission to update this assignment",
          "error",
        );
      } else if (
        errorMessage.includes("validation") ||
        errorMessage.includes("required")
      ) {
        showToast("Please check all required fields and try again", "error");
      } else {
        showToast(errorMessage, "error");
      }
    } finally {
      submittingRef.current = false;
    }
  }, [selectedAssignment, formData, showToast, resetForm, fetchAssignments]);

  const handleDeleteAssignment = useCallback(async () => {
    if (!deletingAssignment) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    try {
      const res = await professionalAssignmentAPI.deleteAssignment(
        deletingAssignment._id,
      );
      if (res?.success) {
        showToast("Assignment deleted successfully", "success");
        setDeletingAssignment(null);
        fetchAssignments();
        fetchRevokedAssignments(); // Refresh revoked assignments list
      } else {
        showToast(res?.message || "Failed to delete assignment", "error");
      }
    } catch (err) {
      console.error("Delete assignment error:", err);

      // Extract meaningful error message from APIError
      let errorMessage = "Failed to delete assignment";

      if (err?.originalData?.error) {
        errorMessage = err.originalData.error;
      } else if (err?.originalData?.message) {
        errorMessage = err.originalData.message;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.details?.message) {
        errorMessage = err.details.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      }

      // Show specific error messages for common issues
      if (errorMessage.includes("not found")) {
        showToast(
          "Assignment not found or may have already been deleted",
          "error",
        );
      } else if (
        errorMessage.includes("not owned") ||
        errorMessage.includes("permission")
      ) {
        showToast(
          "You do not have permission to delete this assignment",
          "error",
        );
      } else if (
        errorMessage.includes("active") ||
        errorMessage.includes("cannot")
      ) {
        showToast("Cannot delete assignment while it is active", "error");
      } else {
        showToast(errorMessage, "error");
      }
    } finally {
      submittingRef.current = false;
    }
  }, [deletingAssignment, showToast, fetchAssignments]);

  /* ── Modal handlers ─────────────────────────────────────────────────── */
  const openEditModal = useCallback(
    async (assignment) => {
      setSelectedAssignment(assignment);

      // Load companies if not already loaded
      if (availableCompanies.length === 0) {
        await fetchAvailableCompanies("");
      }

      // Find professional option (single professional for edit)
      const professionalOption = availableProfessionals.find(
        (p) => p.value === assignment.professional._id,
      );

      // Find company options
      const companyOptions = assignment.assignedCompanies
        .map((ac) => {
          // ac.company may be a populated doc, a raw id, or null (deleted company).
          const companyId = ac.company?._id || ac.company;
          const companyOption = availableCompanies.find(
            (c) => c.value === companyId,
          );
          return companyOption
            ? { ...companyOption, accessLevel: ac.accessLevel }
            : null;
        })
        .filter(Boolean);

      setFormData({
        professionalId: professionalOption ? [professionalOption] : [],
        assignmentScope: assignment.assignmentScope,
        accessLevel: assignment.accessLevel,
        assignedCompanies: companyOptions,
        validFrom: assignment.validFrom
          ? new Date(assignment.validFrom).toISOString().slice(0, 16)
          : "",
        validUntil: assignment.validUntil
          ? new Date(assignment.validUntil).toISOString().slice(0, 16)
          : "",
        notes: assignment.notes || "",
      });
      setShowEditModal(true);
    },
    [availableProfessionals, availableCompanies, fetchAvailableCompanies],
  );

  const openDetailsModal = useCallback((assignment) => {
    setSelectedAssignment(assignment);
    setShowDetailsModal(true);
  }, []);

  /* ── Company selection handlers ─────────────────────────────────────── */
  const handleCompanySelection = useCallback((selectedCompanies) => {
    setFormData((prev) => ({
      ...prev,
      assignedCompanies: selectedCompanies,
    }));
  }, []);

  /* ── Status change handler ─────────────────────────────────────────── */
  const handleStatusChange = useCallback(
    async (assignmentId, newStatus) => {
      if (rowBusyRef.current) return;
      rowBusyRef.current = true;
      try {
        const res = await professionalAssignmentAPI.updateAssignment(
          assignmentId,
          {
            status: newStatus,
          },
        );

        if (res?.success) {
          showToast(`Assignment status updated to ${newStatus}`, "success");
          fetchAssignments();
        } else {
          showToast(res?.message || "Failed to update status", "error");
        }
      } catch (err) {
        console.error("Status update error:", err);

        // Extract meaningful error message from APIError
        let errorMessage = "Failed to update status";

        if (err?.originalData?.error) {
          errorMessage = err.originalData.error;
        } else if (err?.originalData?.message) {
          errorMessage = err.originalData.message;
        } else if (err?.message) {
          errorMessage = err.message;
        } else if (err?.details?.message) {
          errorMessage = err.details.message;
        } else if (typeof err === "string") {
          errorMessage = err;
        }

        // Show specific error messages for common issues
        if (errorMessage.includes("not found")) {
          showToast("Assignment not found or may have been deleted", "error");
        } else if (
          errorMessage.includes("not owned") ||
          errorMessage.includes("permission")
        ) {
          showToast(
            "You do not have permission to update this assignment",
            "error",
          );
        } else if (errorMessage.includes("transition")) {
          showToast(
            "Cannot change status due to current assignment state",
            "error",
          );
        } else {
          showToast(errorMessage, "error");
        }
      } finally {
        rowBusyRef.current = false;
      }
    },
    [fetchAssignments, showToast],
  );

  /* ── Access level change handler ───────────────────────────────────── */
  const handleAccessLevelChange = useCallback(
    async (assignmentId, newAccessLevel) => {
      if (rowBusyRef.current) return;
      rowBusyRef.current = true;
      try {
        const res = await professionalAssignmentAPI.updateAssignment(
          assignmentId,
          {
            accessLevel: newAccessLevel,
          },
        );

        if (res?.success) {
          showToast(
            `Access level updated to ${newAccessLevel.replace("_", " ")}`,
            "success",
          );
          fetchAssignments();
        } else {
          showToast(res?.message || "Failed to update access level", "error");
        }
      } catch (err) {
        console.error("Access level update error:", err);

        // Extract meaningful error message from APIError
        let errorMessage = "Failed to update access level";

        if (err?.originalData?.error) {
          errorMessage = err.originalData.error;
        } else if (err?.originalData?.message) {
          errorMessage = err.originalData.message;
        } else if (err?.message) {
          errorMessage = err.message;
        } else if (err?.details?.message) {
          errorMessage = err.details.message;
        } else if (typeof err === "string") {
          errorMessage = err;
        }

        // Show specific error messages for common issues
        if (errorMessage.includes("not found")) {
          showToast("Assignment not found or may have been deleted", "error");
        } else if (
          errorMessage.includes("not owned") ||
          errorMessage.includes("permission")
        ) {
          showToast(
            "You do not have permission to update this assignment",
            "error",
          );
        } else if (
          errorMessage.includes("accepted") ||
          errorMessage.includes("active")
        ) {
          showToast(
            "Cannot change access level while assignment is active",
            "error",
          );
        } else {
          showToast(errorMessage, "error");
        }
      } finally {
        rowBusyRef.current = false;
      }
    },
    [fetchAssignments, showToast],
  );

  /* ── Derived states ─────────────────────────────────────────────────── */
  const selectedCompanies = useMemo(() => {
    return formData.assignedCompanies;
  }, [formData.assignedCompanies]);

  const selectedProfessionals = useMemo(() => {
    return formData.professionalId;
  }, [formData.professionalId]);

  // Professional statistics
  const statistics = useMemo(() => {
    const stats = {
      total: assignments.length,
      active: assignments.filter((a) => a.status === "active").length,
      pending: assignments.filter((a) => a.professionalStatus === "pending")
        .length,
      accepted: assignments.filter((a) => a.professionalStatus === "accepted")
        .length,
      rejected: assignments.filter((a) => a.professionalStatus === "rejected")
        .length,
      allCompanies: assignments.filter(
        (a) => a.assignmentScope === "all_companies",
      ).length,
      specificCompanies: assignments.filter(
        (a) => a.assignmentScope === "specific_companies",
      ).length,
    };
    return stats;
  }, [assignments]);

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const STATS = [
    { label: "Total Assignments", value: statistics.total, color: "#145f5f", icon: FiUsers },
    { label: "Active", value: statistics.active, color: "#4a6b3f", icon: FiActivity },
    { label: "Pending Response", value: statistics.pending, color: "#836016", icon: FiClock },
    { label: "Accepted", value: statistics.accepted, color: "#4a6b3f", icon: FiUserCheck },
    { label: "All Companies", value: statistics.allCompanies, color: "#3a4a66", icon: FiLayers },
    { label: "Specific Companies", value: statistics.specificCompanies, color: "#b8973a", icon: FiTarget },
  ];

  /* ── Render ─────────────────────────────────────────────────────────── */
  return (
    <AppLayout pageTitle="Manage Collaborators">
      {/* ── Page header ───────────────────────────────────────────────── */}
      <PageHeader
        title="Manage Collaborators"
        subtitle="Manage the CA/CS professionals and collaborators on your companies and control their access levels."
        actions={activeTab !== "access" && (
          <Button onClick={() => setShowCreateModal(true)} leftIcon={<FiPlus size={15} />}>
            Add Professional
          </Button>
        )}
      />

      {/* ── Statistics cards (hidden on the Company Access tab) ───────── */}
      {activeTab !== "access" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5 mb-6 animate-fade-in">
          {STATS.map((s) => {
            const StatIcon = s.icon;
            return (
              <div
                key={s.label}
                className="cs-resolution-card rounded-2xl p-4 flex flex-col justify-between"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="shrink-0 grid place-items-center w-7 h-7 rounded-xl bg-[#fdfbf7] border border-[#e2d9c8] text-[#8a6a1f]">
                    <StatIcon size={14} />
                  </span>
                  <div
                    className="text-[26px] font-bold leading-none"
                    style={{
                      color: s.color,
                      fontFamily: "'Cormorant Garamond', serif",
                    }}
                  >
                    {s.value}
                  </div>
                </div>
                <div
                  className="text-[10px] font-extrabold uppercase tracking-[0.5px] mt-1"
                  style={{ color: "#7a7060", fontFamily: "'Outfit', sans-serif" }}
                >
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Tabs and Items Per Page ─────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        {/* Segmented Pill Selector */}
        <div className="flex items-center gap-1.5 p-1 rounded-xl bg-[#faf7f2] border border-[#e2d9c8] overflow-x-auto cs-premium-scrollbar">
          {[
            { id: "active", label: `Active Assignments (${pagination.total})` },
            { id: "revoked", label: `Revoked Access (${revokedPagination.total})` },
            { id: "access", label: `Company Access (${accessCount})` },
          ].map((t) => {
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                className={`px-3.5 py-1.5 rounded-lg text-[12px] font-bold transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-[#145f5f] text-white shadow-sm'
                    : 'text-[#5a5145] hover:bg-[#faf9f6]'
                }`}
                style={{ fontFamily: "'Outfit', sans-serif" }}
                onClick={() => {
                  if (t.id === "active") {
                    setActiveTab("active");
                    setPage(1);
                  } else if (t.id === "revoked") {
                    setActiveTab("revoked");
                    setRevokedPage(1);
                  } else {
                    setActiveTab("access");
                  }
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Items Per Page */}
        {activeTab !== "access" && (
          <div className="flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <span className="text-[12px] font-medium" style={{ color: "#7a7060" }}>
              Show
            </span>
            <div className="w-[76px]">
              <FormSelect
                size="sm"
                value={String(itemsPerPage)}
                onChange={(v) => {
                  setItemsPerPage(Number(v));
                  setPage(1);
                  setRevokedPage(1);
                }}
                options={[5, 10, 20, 25, 30].map((n) => ({
                  value: String(n),
                  label: String(n),
                }))}
              />
            </div>
            <span className="text-[12px] font-medium" style={{ color: "#7a7060" }}>
              per page
            </span>
          </div>
        )}
      </div>

      {/* ── Filters (show only for active tab) ─────────────────────────────────────── */}
      {activeTab === "active" && (
        <div className="flex items-center gap-3 mb-5 flex-wrap animate-fade-in">
          {/* Status filter */}
          <div className="w-[150px]">
            <FormSelect
              size="sm"
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
              options={[
                { value: "", label: "All Status" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
                { value: "suspended", label: "Suspended" },
              ]}
            />
          </div>

          {/* Professional status filter */}
          <div className="w-[150px]">
            <FormSelect
              size="sm"
              value={professionalStatusFilter}
              onChange={(v) => {
                setProfessionalStatusFilter(v);
                setPage(1);
              }}
              options={[
                { value: "", label: "All Response" },
                { value: "pending", label: "Pending" },
                { value: "accepted", label: "Accepted" },
                { value: "rejected", label: "Rejected" },
              ]}
            />
          </div>

          {/* Scope filter */}
          <div className="w-[170px]">
            <FormSelect
              size="sm"
              value={scopeFilter}
              onChange={(v) => {
                setScopeFilter(v);
                setPage(1);
              }}
              options={[
                { value: "", label: "All Scope" },
                { value: "all_companies", label: "All Companies" },
                { value: "specific_companies", label: "Specific Companies" },
              ]}
            />
          </div>
        </div>
      )}

      {/* ── Company Access tab — incoming access requests & grants ──────── */}
      {activeTab === "access" ? (
        <CompanyAccessPanel onCountChange={setAccessCount} />
      ) : (
        <>
          <div className="space-y-3 animate-fade-in">
            {activeTab === "active" ? (
              loading ? (
                <div className="bg-white border border-[#e7e2d8] rounded-[16px] p-8 text-center animate-pulse flex flex-col items-center justify-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#e7e2d8]" />
                  <div className="h-4 bg-[#e7e2d8] rounded w-36" />
                  <div className="h-3 bg-[#ece8df] rounded w-64" />
                </div>
              ) : assignments.length === 0 ? (
                <div className="bg-white border border-[#e2d9c8] rounded-[16px] px-5 py-14 text-center">
                  <div className="flex justify-center mb-2"><FiUsers size={30} style={{ color: '#b3a995' }} /></div>
                  <div className="text-[14px] font-bold text-[#08111f] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    No assignments yet
                  </div>
                  <div className="text-[12px] mb-4" style={{ color: "#7a7060", fontFamily: "'Outfit', sans-serif" }}>
                    Start by assigning a professional to your companies.
                  </div>
                  <Button size="sm" onClick={() => setShowCreateModal(true)} className="!rounded-xl">
                    Assign Professional
                  </Button>
                </div>
              ) : (
                assignments.map((assignment) => {
                  const statusBadge =
                    STATUS_BADGE[assignment.status] || STATUS_BADGE.active;
                  const professionalStatusBadge =
                    PROFESSIONAL_STATUS_BADGE[assignment.professionalStatus] ||
                    PROFESSIONAL_STATUS_BADGE.pending;
                  const accessBadge =
                    ACCESS_LEVEL_BADGE[assignment.accessLevel] ||
                    ACCESS_LEVEL_BADGE.read;
                  const scopeBadge =
                    ASSIGNMENT_SCOPE_BADGE[assignment.assignmentScope] ||
                    ASSIGNMENT_SCOPE_BADGE.specific_companies;

                  return (
                    <div
                      key={assignment._id}
                      className="cs-resolution-card group rounded-[16px] p-4 flex items-center justify-between gap-4 flex-wrap md:flex-nowrap"
                    >
                      {/* Left profile: Initials Avatar + name details */}
                      <div className="flex items-center gap-3.5 flex-1 min-w-0">
                        <span className="shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-[#edeef3] border border-[#d8dce8] text-[#3a4a66] text-[12px] font-extrabold transition-all duration-300 group-hover:scale-105" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {getInitials(assignment.professional.fullName)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-[13.5px] text-[#08111f]" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' }}>
                            {assignment.professional.fullName}
                          </div>
                          <div className="text-[11px] text-[#7a7060] mt-1 flex items-center gap-2 flex-wrap" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            <span className="font-semibold text-[#145f5f]">{assignment.professional.profession}</span>
                            <span className="text-[#ddd6c6]">•</span>
                            <span>{assignment.professional.email}</span>
                          </div>
                        </div>
                      </div>

                      {/* Middle actions: badges */}
                      <div className="flex items-center gap-3 shrink-0 flex-wrap sm:flex-nowrap">
                        {/* Scope */}
                        <Chip bg={scopeBadge.bg} color={scopeBadge.color}>{scopeBadge.label}</Chip>

                        {/* Access Level */}
                        <Chip bg={accessBadge.bg} color={accessBadge.color}>{accessBadge.label}</Chip>

                        {/* Status */}
                        <Chip bg={statusBadge.bg} color={statusBadge.color}>{statusBadge.label}</Chip>

                        {/* Response */}
                        <Chip bg={professionalStatusBadge.bg} color={professionalStatusBadge.color}>{professionalStatusBadge.label}</Chip>

                        {/* Action buttons list */}
                        <div className="flex items-center gap-1.5 ml-2">
                          <Button
                            size="sm"
                            iconOnly
                            className="cs-btn--header-action"
                            onClick={() => {
                              setSelectedAssignment(assignment);
                              setShowDetailsModal(true);
                            }}
                            title="View details"
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </Button>
                          <Button
                            size="sm"
                            iconOnly
                            className="cs-btn--header-action"
                            onClick={() => navigate(`/manage-collaborators/${assignment._id}/activity`)}
                            title="View activity on your companies"
                          >
                            <FiActivity size={15} />
                          </Button>
                          <Button
                            size="sm"
                            iconOnly
                            className="cs-btn--header-action"
                            onClick={() => {
                              setSelectedAssignment(assignment);
                              setShowEditModal(true);
                            }}
                            title="Edit assignment"
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </Button>
                          <Button
                            size="sm"
                            iconOnly
                            className="cs-btn--header-action"
                            onClick={() => setDeletingAssignment(assignment)}
                            title="Delete assignment"
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                              <line x1="10" y1="11" x2="10" y2="17" />
                              <line x1="14" y1="11" x2="14" y2="17" />
                            </svg>
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )
            ) : revokedLoading ? (
              <div className="bg-white border border-[#e7e2d8] rounded-[16px] p-8 text-center animate-pulse flex flex-col items-center justify-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#e7e2d8]" />
                <div className="h-4 bg-[#e7e2d8] rounded w-36" />
                <div className="h-3 bg-[#ece8df] rounded w-64" />
              </div>
            ) : revokedAssignments.length === 0 ? (
              <div className="bg-white border border-[#e2d9c8] rounded-[16px] px-5 py-14 text-center">
                <div className="flex justify-center mb-2"><FiLock size={30} style={{ color: '#b3a995' }} /></div>
                <div className="text-[14px] font-bold text-[#08111f] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  No revoked access
                </div>
                <div className="text-[12px]" style={{ color: "#7a7060", fontFamily: "'Outfit', sans-serif" }}>
                  No users have had their access revoked yet.
                </div>
              </div>
            ) : (
              revokedAssignments.map((assignment) => {
                const accessBadge =
                  ACCESS_LEVEL_BADGE[assignment.accessLevel] ||
                  ACCESS_LEVEL_BADGE.read;
                const scopeBadge =
                  ASSIGNMENT_SCOPE_BADGE[assignment.assignmentScope] ||
                  ASSIGNMENT_SCOPE_BADGE.specific_companies;

                return (
                  <div
                    key={assignment._id}
                    className="cs-resolution-card group rounded-[16px] p-4 flex items-center justify-between gap-4 flex-wrap md:flex-nowrap"
                  >
                    {/* Left profile: Initials Avatar + name details */}
                    <div className="flex items-center gap-3.5 flex-1 min-w-0">
                      <span className="shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-[#edeef3] border border-[#d8dce8] text-[#3a4a66] text-[12px] font-extrabold transition-all duration-300 group-hover:scale-105" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {getInitials(assignment.professional.fullName)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-[13.5px] text-[#08111f]" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' }}>
                          {assignment.professional.fullName}
                        </div>
                        <div className="text-[11px] text-[#7a7060] mt-1 flex items-center gap-2 flex-wrap" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          <span className="font-semibold text-[#145f5f]">{assignment.professional.profession}</span>
                          <span className="text-[#ddd6c6]">•</span>
                          <span>{assignment.professional.email}</span>
                        </div>
                      </div>
                    </div>

                    {/* Middle actions: badges */}
                    <div className="flex items-center gap-3 shrink-0 flex-wrap sm:flex-nowrap">
                      {/* Scope */}
                      <Chip bg={scopeBadge.bg} color={scopeBadge.color}>{scopeBadge.label}</Chip>

                      {/* Access Level */}
                      <Chip bg={accessBadge.bg} color={accessBadge.color}>{accessBadge.label}</Chip>

                      {/* Status - Always revoked for this tab */}
                      <Chip bg="#f2e9e6" color="#9a564a">Revoked</Chip>

                      {/* Revoked Date */}
                      <span className="text-[11.5px]" style={{ color: '#7a7060', fontFamily: "'Outfit', sans-serif" }}>
                        {formatDate(assignment.revokedAt)}
                      </span>

                      {/* Action buttons list */}
                      <div className="flex items-center gap-1.5 ml-2">
                        <Button
                          size="sm"
                          iconOnly
                          className="cs-btn--header-action"
                          onClick={() => {
                            setSelectedAssignment(assignment);
                            setShowDetailsModal(true);
                          }}
                          title="View details"
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* ── Pagination ─────────────────────────────────────────────────── */}
          <div
            className="flex items-center justify-between mt-4 pt-4 border-t"
            style={{ borderColor: "#ddd6c6" }}
          >
            <div className="text-[12px]" style={{ color: "#7a7060" }}>
              {activeTab === "active"
                ? `Showing ${assignments.length} of ${pagination.total} assignments`
                : `Showing ${revokedAssignments.length} of ${revokedPagination.total} revoked users`}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (activeTab === "active") {
                    setPage((p) => Math.max(1, p - 1));
                  } else {
                    setRevokedPage((p) => Math.max(1, p - 1));
                  }
                }}
                disabled={
                  activeTab === "active"
                    ? !pagination.hasPrev
                    : !revokedPagination.hasPrev
                }
                className="px-3 py-1.5 rounded-[8px] border text-[12px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:bg-[#f8f5ef]"
                style={{ borderColor: "#ddd6c6", color: "#08111f" }}
              >
                Previous
              </button>
              <span className="text-[12px]" style={{ color: "#7a7060" }}>
                Page {activeTab === "active" ? page : revokedPage} of{" "}
                {activeTab === "active"
                  ? pagination.pages
                  : revokedPagination.pages}
              </span>
              <button
                onClick={() => {
                  if (activeTab === "active") {
                    setPage((p) => Math.min(pagination.pages, p + 1));
                  } else {
                    setRevokedPage((p) =>
                      Math.min(revokedPagination.pages, p + 1),
                    );
                  }
                }}
                disabled={
                  activeTab === "active"
                    ? !pagination.hasNext
                    : !revokedPagination.hasNext
                }
                className="px-3 py-1.5 rounded-[8px] border text-[12px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:bg-[#f8f5ef]"
                style={{ borderColor: "#ddd6c6", color: "#08111f" }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Create Assignment Modal ─────────────────────────────────────── */}
      {showCreateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            background: "rgba(8,17,31,0.55)",
          }}
        >
          <div
            className="bg-white rounded-[12px] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            style={{ border: "1px solid #ddd6c6" }}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-[18px] font-bold text-[#08111f]">
                  Assign Professional
                </h2>
                <button
                  type="button"
                  aria-label="Close"
                  title="Close"
                  className="group w-8 h-8 rounded-[10px] flex items-center justify-center text-[#7a7060] transition-all duration-200 hover:bg-[#9a564a]/10 hover:text-[#9a564a] active:scale-90"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                >
                  <FiX
                    size={17}
                    className="transition-transform duration-300 group-hover:rotate-90"
                  />
                </button>
              </div>

              {/* Professional Selection */}
              <div className="mb-4">
                <label className={labelClass}>
                  Select Professional
                </label>
                <Select
                  value={selectedProfessionals}
                  onChange={(selected) =>
                    setFormData((prev) => ({
                      ...prev,
                      professionalId: selected,
                    }))
                  }
                  options={availableProfessionals}
                  styles={selectStyles}
                  placeholder="Search professionals..."
                  isLoading={professionalsLoading}
                  isClearable
                  isSearchable
                  isMulti
                  onInputChange={(inputValue, { action }) => {
                    if (action === "input-change") {
                      if (inputValue.trim().length >= 1) {
                        fetchAvailableProfessionals(inputValue);
                      } else {
                        setAvailableProfessionals([]);
                      }
                    }
                  }}
                  noOptionsMessage={({ inputValue }) => {
                    if (inputValue) {
                      return (
                        <div className="p-2">
                          <div className="text-gray-500 text-sm mb-2">
                            No professionals found
                          </div>
                          <button
                            type="button"
                            className="w-full px-3 py-2 text-[11px] text-white rounded transition-colors text-left"
                            style={{ backgroundColor: '#145f5f' }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowInviteModal(true);
                            }}
                          >
                            <FiMail size={13} className="inline-block mr-1 -mt-0.5" /> Invite New Professional
                          </button>
                        </div>
                      );
                    }
                    return "Type to search professionals...";
                  }}
                />
              </div>

              {/* Assignment Scope */}
              <div className="mb-4">
                <label className={labelClass}>
                  Assignment Scope
                </label>
                <div className="flex gap-3">
                  {[
                    {
                      value: "specific_companies",
                      label: "Specific Companies",
                    },
                    { value: "all_companies", label: "All Companies" },
                  ].map((scope) => (
                    <label
                      key={scope.value}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="scope"
                        value={scope.value}
                        checked={formData.assignmentScope === scope.value}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            assignmentScope: e.target.value,
                          }))
                        }
                        className="text-[12px]"
                      />
                      <span className="text-[12px]">{scope.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Access Level */}
              <div className="mb-4">
                <label className={labelClass}>
                  Access Level
                </label>
                <FormSelect
                  value={formData.accessLevel}
                  onChange={(v) =>
                    setFormData((prev) => ({ ...prev, accessLevel: v }))
                  }
                  options={[
                    { value: "read", label: "Read Only" },
                    { value: "read_write", label: "Read & Write" },
                    { value: "admin", label: "Admin Access" },
                  ]}
                />
              </div>

              {/* Company Selection (for specific_companies scope) */}
              {formData.assignmentScope === "specific_companies" && (
                <div className="mb-4">
                  <label className={labelClass}>
                    Select Companies
                  </label>
                  <Select
                    value={selectedCompanies}
                    onChange={handleCompanySelection}
                    options={availableCompanies}
                    styles={selectStyles}
                    placeholder="Search companies..."
                    isLoading={companiesLoading}
                    isClearable
                    isSearchable
                    isMulti
                    noOptionsMessage={() => "No companies found"}
                  />
                </div>
              )}

              {/* Validity Period */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelClass}>
                    Valid From
                  </label>
                  <input
                    type="datetime-local"
                    className={inputClass}
                    value={formData.validFrom}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        validFrom: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Valid Until (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    className={inputClass}
                    value={formData.validUntil}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        validUntil: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="mb-6">
                <label className={labelClass}>
                  Notes (Optional)
                </label>
                <textarea
                  className={textareaClass}
                  placeholder="Add any notes about this assignment..."
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={handleCreateAssignment}>
                  Create Assignment
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Assignment Modal ─────────────────────────────────────── */}
      {showEditModal && selectedAssignment && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            background: "rgba(8,17,31,0.55)",
          }}
        >
          <div
            className="bg-white rounded-[12px] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            style={{ border: "1px solid #ddd6c6" }}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-[18px] font-bold text-[#08111f]">
                  Edit Assignment
                </h2>
                <button
                  type="button"
                  aria-label="Close"
                  title="Close"
                  className="group w-8 h-8 rounded-[10px] flex items-center justify-center text-[#7a7060] transition-all duration-200 hover:bg-[#9a564a]/10 hover:text-[#9a564a] active:scale-90"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedAssignment(null);
                    resetForm();
                  }}
                >
                  <FiX
                    size={17}
                    className="transition-transform duration-300 group-hover:rotate-90"
                  />
                </button>
              </div>

              {/* Professional Info (Read-only) */}
              <div className="mb-4">
                <label className={labelClass}>
                  Professional
                </label>
                <div className="px-3 py-[8px] rounded-[8px] bg-[#f8f5ef] text-[12px]">
                  <div className="font-semibold">
                    {selectedAssignment.professional.fullName}
                  </div>
                  <div className="text-[11px]" style={{ color: "#7a7060" }}>
                    {selectedAssignment.professional.profession} •{" "}
                    {selectedAssignment.professional.email}
                  </div>
                </div>
              </div>

              {/* Assignment Scope */}
              <div className="mb-4">
                <label className={labelClass}>
                  Assignment Scope
                </label>
                <div className="flex gap-3">
                  {[
                    {
                      value: "specific_companies",
                      label: "Specific Companies",
                    },
                    { value: "all_companies", label: "All Companies" },
                  ].map((scope) => (
                    <label
                      key={scope.value}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="scope"
                        value={scope.value}
                        checked={formData.assignmentScope === scope.value}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            assignmentScope: e.target.value,
                          }))
                        }
                        className="text-[12px]"
                      />
                      <span className="text-[12px]">{scope.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Access Level */}
              <div className="mb-4">
                <label className={labelClass}>
                  Access Level
                </label>
                <FormSelect
                  value={formData.accessLevel}
                  onChange={(v) =>
                    setFormData((prev) => ({ ...prev, accessLevel: v }))
                  }
                  options={[
                    { value: "read", label: "Read Only" },
                    { value: "read_write", label: "Read & Write" },
                    { value: "admin", label: "Admin Access" },
                  ]}
                />
              </div>

              {/* Company Selection (for specific_companies scope) */}
              {formData.assignmentScope === "specific_companies" && (
                <div className="mb-4">
                  <label className={labelClass}>
                    Assigned Companies
                  </label>
                  <Select
                    value={selectedCompanies}
                    onChange={handleCompanySelection}
                    options={availableCompanies}
                    styles={selectStyles}
                    placeholder="Search companies..."
                    isLoading={companiesLoading}
                    isClearable
                    isSearchable
                    isMulti
                    noOptionsMessage={() => "No companies found"}
                  />
                </div>
              )}

              {/* Validity Period */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelClass}>
                    Valid From
                  </label>
                  <input
                    type="datetime-local"
                    className={inputClass}
                    value={formData.validFrom}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        validFrom: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Valid Until (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    className={inputClass}
                    value={formData.validUntil}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        validUntil: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="mb-6">
                <label className={labelClass}>
                  Notes (Optional)
                </label>
                <textarea
                  className={textareaClass}
                  placeholder="Add any notes about this assignment..."
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedAssignment(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={handleUpdateAssignment}>
                  Update Assignment
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Assignment Details Modal ─────────────────────────────────────── */}
      {showDetailsModal && selectedAssignment && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            background: "rgba(8,17,31,0.55)",
          }}
        >
          <div
            className="bg-white rounded-[12px] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            style={{ border: "1px solid #ddd6c6" }}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-[18px] font-bold text-[#08111f]">
                  Assignment Details
                </h2>
                <button
                  type="button"
                  aria-label="Close"
                  title="Close"
                  className="group w-8 h-8 rounded-[10px] flex items-center justify-center text-[#7a7060] transition-all duration-200 hover:bg-[#9a564a]/10 hover:text-[#9a564a] active:scale-90"
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedAssignment(null);
                  }}
                >
                  <FiX
                    size={17}
                    className="transition-transform duration-300 group-hover:rotate-90"
                  />
                </button>
              </div>

              {/* Professional Details */}
              <div className="mb-6">
                <h3 className="text-[14px] font-semibold text-[#08111f] mb-3">
                  Professional Details
                </h3>
                <div className="bg-[#f8f5ef] rounded-[8px] p-4">
                  <div className="grid grid-cols-2 gap-4 text-[12px]">
                    <div>
                      <span className="font-semibold">Name:</span>{" "}
                      {selectedAssignment.professional.fullName}
                    </div>
                    <div>
                      <span className="font-semibold">Email:</span>{" "}
                      {selectedAssignment.professional.email}
                    </div>
                    <div>
                      <span className="font-semibold">Mobile:</span>{" "}
                      {selectedAssignment.professional.mobile}
                    </div>
                    <div>
                      <span className="font-semibold">Profession:</span>{" "}
                      {selectedAssignment.professional.profession}
                    </div>
                    {selectedAssignment.professional.membershipNo && (
                      <div>
                        <span className="font-semibold">Membership No:</span>{" "}
                        {selectedAssignment.professional.membershipNo}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Assignment Details */}
              <div className="mb-6">
                <h3 className="text-[14px] font-semibold text-[#08111f] mb-3">
                  Assignment Details
                </h3>
                <div className="bg-[#f8f5ef] rounded-[8px] p-4">
                  <div className="grid grid-cols-2 gap-4 text-[12px]">
                    <div>
                      <span className="font-semibold">Scope:</span>{" "}
                      {selectedAssignment.assignmentScope === "all_companies"
                        ? "All Companies"
                        : "Specific Companies"}
                    </div>
                    <div>
                      <span className="font-semibold">Access Level:</span>{" "}
                      {selectedAssignment.accessLevel
                        .replace("_", " ")
                        .toUpperCase()}
                    </div>
                    <div>
                      <span className="font-semibold">Status:</span>{" "}
                      {selectedAssignment.status.toUpperCase()}
                    </div>
                    <div>
                      <span className="font-semibold">
                        Professional Response:
                      </span>{" "}
                      {selectedAssignment.professionalStatus
                        .replace("_", " ")
                        .toUpperCase()}
                    </div>
                    <div>
                      <span className="font-semibold">Created:</span>{" "}
                      {formatDate(selectedAssignment.createdAt)}
                    </div>
                    <div>
                      <span className="font-semibold">Valid From:</span>{" "}
                      {formatDate(selectedAssignment.validFrom)}
                    </div>
                    {selectedAssignment.validUntil && (
                      <div>
                        <span className="font-semibold">Valid Until:</span>{" "}
                        {formatDate(selectedAssignment.validUntil)}
                      </div>
                    )}
                    {selectedAssignment.professionalResponseAt && (
                      <div>
                        <span className="font-semibold">Response Date:</span>{" "}
                        {formatDate(selectedAssignment.professionalResponseAt)}
                      </div>
                    )}
                  </div>

                  {selectedAssignment.notes && (
                    <div className="mt-3">
                      <span className="font-semibold">Notes:</span>
                      <div
                        className="mt-1 text-[12px]"
                        style={{ color: "#7a7060" }}
                      >
                        {selectedAssignment.notes}
                      </div>
                    </div>
                  )}

                  {selectedAssignment.professionalResponseNotes && (
                    <div className="mt-3">
                      <span className="font-semibold">
                        Professional Response Notes:
                      </span>
                      <div
                        className="mt-1 text-[12px]"
                        style={{ color: "#7a7060" }}
                      >
                        {selectedAssignment.professionalResponseNotes}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Assigned Companies */}
              {selectedAssignment.assignmentScope === "specific_companies" &&
                selectedAssignment.assignedCompanies.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-[14px] font-semibold text-[#08111f] mb-3">
                      Assigned Companies
                    </h3>
                    <div
                      className="border rounded-[8px] overflow-hidden"
                      style={{ borderColor: "#ddd6c6" }}
                    >
                      {selectedAssignment.assignedCompanies
                        .filter((ca) => ca.company)
                        .map((companyAssignment, index, list) => (
                          <div
                            key={companyAssignment.company._id}
                            className={`px-4 py-3 text-[12px] ${index !== list.length - 1 ? "border-b" : ""}`}
                            style={{ borderColor: "#eee6d8" }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-semibold">
                                  {companyAssignment.company.name}
                                </div>
                                <div
                                  className="text-[11px]"
                                  style={{ color: "#7a7060" }}
                                >
                                  CIN: {companyAssignment.company.cin} • Status:{" "}
                                  {companyAssignment.company.status}
                                </div>
                              </div>
                              <div>
                                <span
                                  className="inline-block px-[6px] py-[1px] rounded-[4px] text-[10px] font-bold"
                                  style={{
                                    background: (
                                      ACCESS_LEVEL_BADGE[
                                        companyAssignment.accessLevel
                                      ] || ACCESS_LEVEL_BADGE.read
                                    ).bg,
                                    color: (
                                      ACCESS_LEVEL_BADGE[
                                        companyAssignment.accessLevel
                                      ] || ACCESS_LEVEL_BADGE.read
                                    ).color,
                                  }}
                                >
                                  {
                                    (
                                      ACCESS_LEVEL_BADGE[
                                        companyAssignment.accessLevel
                                      ] || ACCESS_LEVEL_BADGE.read
                                    ).label
                                  }
                                </span>
                              </div>
                            </div>
                            <div
                              className="mt-1 text-[11px]"
                              style={{ color: "#7a7060" }}
                            >
                              Assigned on:{" "}
                              {formatDate(companyAssignment.assignedAt)}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedAssignment(null);
                  }}
                >
                  Close
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setShowDetailsModal(false);
                    openEditModal(selectedAssignment);
                  }}
                >
                  Edit Assignment
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Dialog ───────────────────────────────────── */}
      <ConfirmDialog
        open={Boolean(deletingAssignment)}
        onClose={() => setDeletingAssignment(null)}
        variant="danger"
        title="Delete Assignment?"
        message={
          deletingAssignment ? (
            <>
              Are you sure you want to delete the assignment for{" "}
              <strong>{deletingAssignment.professional.fullName}</strong>? This
              will remove all access granted to this professional.
            </>
          ) : (
            ""
          )
        }
        note="This action cannot be undone. The professional will lose access to all assigned companies."
        confirmLabel="Delete Assignment"
        cancelLabel="Cancel"
        onConfirm={handleDeleteAssignment}
      />

      {/* ── Invite Professional Modal ───────────────────────────────────── */}
      <InviteProfessionalModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onProfessionalCreated={handleProfessionalCreated}
        onProfessionalSelected={handleProfessionalSelected}
      />

    </AppLayout>
  );
}
